import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Show, Typography } from 'shared';
import FileUpload from 'shared_DEPRECATED/components/Form/FileUpload/FileUpload';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import Spacer from 'shared_DEPRECATED/components/Spacer';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import { SessionsFormCoachingToolsMiroBoardsList } from 'features/sessions/components/Form/CoachingTools';
import { MIRO_BOARDS_FIELD_KEY } from 'features/sessions/config';
import { SessionType } from 'features/sessions/config/types';

import { showErrorToast, showSuccessToast } from 'shared/components/Toast';
import { useUploadFiles } from 'shared/hooks/useFileUpload';

type SessionsFormCoachingToolsMiroBoardsProps = {
  formKey: string;
};

export const SessionsFormCoachingToolsMiroBoards = ({
  formKey,
}: SessionsFormCoachingToolsMiroBoardsProps) => {
  const { watch, setValue } = useFormContext<SessionType>();
  const { uploadFiles } = useUploadFiles();
  const [isUploading, setIsUploading] = useState(false);

  const miroBoardsComposedFieldKey = `${formKey}.${MIRO_BOARDS_FIELD_KEY}`;

  const miroBoards: Attachment[] =
    (watch(miroBoardsComposedFieldKey as any) as Attachment[]) || [];

  const handleFileUpload = async (files: File[]) => {
    const pdfFiles = files.filter((file) => file.type === 'application/pdf');

    if (pdfFiles.length !== files.length) {
      showErrorToast('Only PDF files are allowed');
    }

    if (pdfFiles.length === 0) {
      return;
    }

    setIsUploading(true);

    try {
      const uploadedMediaIds = await uploadFiles(
        pdfFiles.map((file) => ({ file, originalFilename: file.name }))
      );

      const newMiroBoards: Attachment[] = pdfFiles.map((file, index) => ({
        url: '',
        name: file.name,
        contentType: file.type,
        contentLength: file.size,
        mediaId: uploadedMediaIds[index],
      }));

      setValue(
        miroBoardsComposedFieldKey as any,
        [...miroBoards, ...newMiroBoards],
        {
          shouldDirty: false,
        }
      );

      showSuccessToast(
        `${pdfFiles.length} file${
          pdfFiles.length > 1 ? 's' : ''
        } uploaded successfully`
      );
    } catch (error) {
      showErrorToast('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = (indexToDelete: number) => {
    const updatedAttachments = miroBoards.filter(
      (_, index) => index !== indexToDelete
    );

    setValue(miroBoardsComposedFieldKey as any, updatedAttachments, {
      shouldDirty: false,
    });
  };

  return (
    <>
      <FileUpload
        onFileUpload={handleFileUpload}
        extensions=".pdf"
        multiple={true}
        disabled={isUploading}
        dataTestid="coaching-tools-pdf-upload"
      >
        <Icon
          name={iconNames.upload}
          stroke={isUploading ? 'var(--fgSecondary)' : 'var(--fgPrimary)'}
          strokeWidth={1}
        />
        <Spacer size="zr xsm" />
        <Typography color="black" fontWeight="medium" type="meta">
          {isUploading ? 'Uploading...' : 'Upload files'}
        </Typography>
      </FileUpload>

      <Show when={miroBoards.length}>
        <SessionsFormCoachingToolsMiroBoardsList
          formKey={miroBoardsComposedFieldKey}
          onDelete={handleDeleteAttachment}
        />
      </Show>
    </>
  );
};
